<script setup lang="ts">

import {computed, ref, watch} from "vue";
import WidgetContainer from "@/components/EditView/widgets/WidgetContainer.vue";
import {WidgetProps} from "@/utils/presenters/interfaces.ts";
import FieldWidgetUtil from "@/utils/editView/widgets/FieldWidgetUtil.ts";
import ErrorTooltip from "@/components/EditView/ErrorTooltip.vue";
import {QInput, QSelect} from "quasar";
import ClientCache from "@/utils/login/ClientCache.ts";
import ObjectChecker from "@/utils/common/ObjectChecker.ts";
import NamaIcon from "@/components/icons/NamaIcon.vue";
import {focusAndSelectInput, NamaNavigateToNextEmit} from "@/utils/common/NamaVueWidget.ts";
import DateFieldUtils from "@/utils/common/DateFieldUtils.ts";

const props = defineProps<WidgetProps>();
const fieldId = props.field.id!;
const fieldUtil = new FieldWidgetUtil(props.presenter, props.fieldMetaData);
const emit = defineEmits<NamaNavigateToNextEmit>()

const padZero = (num: number) => (num < 10 ? `0${num}` : num);
const today = new Date();
const formattedDate = `${today.getFullYear()}/${padZero(today.getMonth() + 1)}`;
const defaultDate = ref(formattedDate);
const dateMenuState = ref<boolean>(false);
const input = ref<QInput | QSelect>();

const inputDate = fieldUtil.formattedValue();
const allowedValues = ClientCache.getAllowedValuesFor(props.presenter.entityType, props.fieldMetaData.fieldId ?? '');
const isRestricted = ClientCache.isFieldValuesRestricted(props.presenter.entityType, props.fieldMetaData.fieldId);
const hasAllowedValues = ObjectChecker.isNotEmptyOrNull(allowedValues);

const selectedDate = ref(inputDate.value);

const selectedDateForTitle = computed(() => {
  return DateFieldUtils.formatDisplay(selectedDate.value, 'dddd, D MMMM');
});

watch(() => inputDate.value, (value) => {
    selectedDate.value = value
  }
);
function onDateSelect(value: any) {
  fieldUtil.onPartialUserInput(value);
  selectedDate.value = value;
  fieldUtil.onBlur();
  dateMenuState.value = false;
}

function focus() {
  focusAndSelectInput(input);
}

function canReceiveFocus() {
  return !fieldUtil.isFieldDisabled(props.forceEnableField);
}

function onDateIconClick() {
  if (isRestricted)
    return;
  dateMenuState.value = true;
}

function onFocus() {
  fieldUtil.onFocus();
  if (!hasAllowedValues || !isRestricted)
    dateMenuState.value = true;
  else
    dateMenuState.value = false;
}

function onEnterKeyDown(e: KeyboardEvent, doNotGoToNextWithEnter?: boolean) {
  dateMenuState.value = false;
  if (doNotGoToNextWithEnter)
    return;
  emit('navigateToNext', {keyboardEvent: e});
}

function onDoubleClick(e: MouseEvent) {
  if (fieldUtil.isFieldDisabled(props.forceEnableField))
    return;
  dateMenuState.value = true
}

defineExpose({focus, canReceiveFocus})

function onSelect(value?: string) {
  fieldUtil.onPartialUserInput(value);
  selectedDate.value = value;
  fieldUtil.onBlur();
}

</script>

<template>
  <WidgetContainer
    :field="field"
    :entityType="presenter.entityType"
    :isSpanned="isSpanned" :isFieldDisabled="fieldUtil.isFieldDisabled(forceEnableField)"
    :isFieldRequired="fieldUtil.isFieldRequired()" :doNotApplyThemeLayouts="doNotApplyThemeLayouts"
    :isInsideComposite="isInsideComposite">
    <template v-slot:widget="{ showAsPassword, doNotGoToNextWithEnter }">
      <q-select
        v-if="hasAllowedValues" :options="allowedValues" hide-bottom-space outlined dense options-dense use-input hide-selected fill-input
        :model-value="inputDate" @update:model-value="onSelect"
        @input-value="fieldUtil.onPartialUserInput"
        :readonly="fieldUtil.isFieldDisabled(forceEnableField)" ref="input"
        @focus="onFocus" @blur="fieldUtil.onBlur"
        :id="field.id"
        autocomplete="off"
        input-debounce="0"
        :type="showAsPassword ? 'password' : 'text'"
        :error="!!props.presenter.fetchFieldErrorMsg(fieldId)"
        class="nama-customize-dense-input"
        :class="{'nama-field-input-disabled':fieldUtil.isFieldDisabled(forceEnableField),'nama-field-input':!fieldUtil.isFieldDisabled(forceEnableField)}"
        input-class="tw-text-base tw-font-normal dark:tw-text-white tw-bg-transparent focus:tw-ring-0"
        options-selected-class="tw-text-base active-white tw-font-normal tw-border-b-black tw-bg-primary-light dark:tw-bg-primary-dark tw-text-white nama-icon-active"
        popup-content-class="!tw-max-h-[30vh] tw-text-base tw-font-normal dark:tw-text-white dark:tw-bg-namaBackground-bright-dark"
        @keydown.enter="(e) => onEnterKeyDown(e, doNotGoToNextWithEnter)"
        @keydown.tab="dateMenuState = false">
        <template v-slot:append>
          <q-avatar square class="tw-cursor-pointer nama-avatar-hover" :clickable="false">
            <NamaIcon default-icon="nm-calendar" class="tw-size-4" @click.stop.prevent="onDateIconClick"
                      :class="[isRestricted ? 'icon-disabled' : 'active-primary']"/>
            <q-menu v-model="dateMenuState" ref="menuRef" anchor="bottom left" self="top left" no-focus no-refocus no-parent-event
                    class="tw-bg-transparent">
              <q-date mask="DD-MM-YYYY" :model-value="selectedDate" @update:model-value="onDateSelect" :title="selectedDateForTitle"
                      :readonly="fieldUtil.isFieldDisabled(forceEnableField)"
                      :default-year-month="defaultDate" today-btn
                      class="nama-date tw-text-namaBlack tw-bg-namaBackground-deep-light dark:tw-text-namaFontColor-input-dark dark:tw-bg-namaBackground-dim-dark">
              </q-date>
            </q-menu>
          </q-avatar>
        </template>
        <ErrorTooltip v-if="presenter.fetchFieldErrorMsg(fieldId)" :error-msg="presenter.fetchFieldErrorMsg(fieldId)"/>
      </q-select>

      <q-input
        v-else hide-bottom-space outlined dense :model-value="inputDate" @update:model-value="fieldUtil.onPartialUserInput"
        :readonly="fieldUtil.isFieldDisabled(forceEnableField)" ref="input"
        @focus="onFocus" @blur="fieldUtil.onBlur"
        :id="field.id"
        @dblclick="onDoubleClick"
        autocomplete="off"
        :type="showAsPassword ? 'password' : 'text'"
        :error="!!props.presenter.fetchFieldErrorMsg(fieldId)"
        class="nama-customize-dense-input nama-focused-input nama-dark-popup"
        :class="{'nama-field-input-disabled':fieldUtil.isFieldDisabled(forceEnableField),'nama-field-input':!fieldUtil.isFieldDisabled(forceEnableField)}"
        input-class="nama-input-class"
        @keydown.enter="(e) => onEnterKeyDown(e, doNotGoToNextWithEnter)"
        @keydown.tab="dateMenuState = false">
        <template v-slot:append>
          <q-avatar square class="tw-cursor-pointer nama-avatar-hover" :clickable="false">
            <NamaIcon default-icon="nm-calendar" class="tw-size-4 active-primary" @click.stop.prevent="onDateIconClick"/>
            <q-menu v-model="dateMenuState" ref="menuRef" anchor="bottom left" self="top left" no-focus no-refocus no-parent-event
                    class="tw-bg-transparent">
              <q-date mask="DD-MM-YYYY" :model-value="selectedDate" @update:model-value="onDateSelect" :title="selectedDateForTitle"
                      :readonly="fieldUtil.isFieldDisabled(forceEnableField)"
                      :default-year-month="defaultDate" today-btn
                      class="nama-date tw-text-namaBlack tw-bg-namaBackground-deep-light dark:tw-text-namaFontColor-input-dark dark:tw-bg-namaBackground-dim-dark">
              </q-date>
            </q-menu>
          </q-avatar>
        </template>
        <ErrorTooltip v-if="presenter.fetchFieldErrorMsg(fieldId)" :error-msg="presenter.fetchFieldErrorMsg(fieldId)"/>
      </q-input>
    </template>
  </WidgetContainer>
</template>
<style>
</style>
